import Axios from 'axios';
import { ICreatePageDTO } from '../dtos/create-page-dto';
import {
  documentQuery,
  documentTrackingQuery,
  loadDocumentPageQuery,
  documentPreviewTokenQuery,
  pageThumbUrl,
  pageNumQuery,
  templateResourceDetailsQuery,
  documentRevisionsQuery,
  externalEditorIdQuery,
} from '@/api/documents/graphql/document.queries';
import { IDocumentTracking } from '@/document-tracking/types/document-tracking';
import { apolloClient } from '@/apollo';
import { correctDates } from '@/dealroom/services/dealroom.service';
import { IDocument } from '@/types/Document';
import { IDocumentPage } from '@/types/document-page';
import { ISession } from '@/types/Session';
import { IPreviewToken } from '@/dealroom/document/types/preview-token';
import { IUploadFileUrl } from '@/types/upload-file-url';
import {
  addDocumentPageMutation,
  getUploadFilesUrlsMutation,
  removeDocumentPageMutation,
  updateDocumentFromTemplateMutation,
  updateDocumentPageMutation,
  uploadFilesDoneMutation,
} from '@/graphql/mutations/dealroom.mutations';
import { IPublishDocumentPayload } from '@/dealroom-view/types';
import {
  discardChangesMutation,
  publishDocumentMutation,
} from '@/api/documents/graphql/document.mutations';
import { IDocumentRevision } from '@/types/document-revision';
import { IUpdatePageDTO } from '@/api/documents/dtos/update-page-dto';
import { EFetchPolicy } from '@/api/types/enums/fetch-policy';
import { inProduction } from '@/common/helpers';
import { documentPageQuery } from '@/graphql/queries/dealroom.queries';

export class DocumentService {
  static async loadDocument(documentId: string): Promise<IDocument> {
    const {
      data: { document },
    } = await apolloClient.query({
      query: documentQuery,
      variables: { documentId },
      fetchPolicy: EFetchPolicy.NetworkOnly,
    });
    return correctDates(document);
  }

  static async loadDocumentRevisions(documentId: string): Promise<IDocumentRevision[]> {
    const {
      data: {
        document: { documentRevisions },
      },
    } = await apolloClient.query({
      query: documentRevisionsQuery,
      variables: { documentId },
      fetchPolicy: EFetchPolicy.NetworkOnly,
    });
    return documentRevisions;
  }

  static async loadDocumentPage(documentId: string, id: string): Promise<IDocumentPage> {
    const {
      data: { documentPage },
    } = await apolloClient.query({
      query: loadDocumentPageQuery,
      variables: { documentId, id },
      fetchPolicy: EFetchPolicy.NetworkOnly,
    });
    return documentPage;
  }

  static async publishDocument({ documentId, message }: IPublishDocumentPayload): Promise<boolean> {
    const {
      data: { publishDocument },
    } = await apolloClient.mutate({
      mutation: publishDocumentMutation,
      variables: { documentId, message },
    });
    return publishDocument;
  }

  static async loadPages(documentId: string): Promise<IDocumentPage[]> {
    const {
      data: { document },
    } = await apolloClient.query({
      query: documentPageQuery,
      variables: { documentId },
      fetchPolicy: EFetchPolicy.NetworkOnly,
    });
    return document.pages;
  }

  static async discardChanges(documentId: string): Promise<boolean> {
    const {
      data: { discardChanges },
    } = await apolloClient.mutate({
      mutation: discardChangesMutation,
      variables: { documentId },
    });
    return discardChanges;
  }

  static async loadPageThumbUrl(
    documentPageId: string,
    documentId: string
  ): Promise<Partial<IDocumentPage>> {
    const {
      data: { documentPage },
    } = await apolloClient.query({
      query: pageThumbUrl,
      variables: { id: documentPageId, documentId },
      fetchPolicy: EFetchPolicy.CacheFirst,
    });
    return documentPage;
  }

  static async createSampleDocument(session: ISession, templateId?: string): Promise<string> {
    const payload = new FormData();
    let fallbackTempId = '5qdv4u5vrm2x';
    if (!inProduction(window)) {
      fallbackTempId = 'n2f9n5v5';
    }
    payload.append('document-id', templateId ? templateId : fallbackTempId);
    const {
      data: { document_id, error },
    } = await Axios.post('/get/sample/document', payload);
    if (error) {
      throw new Error(error);
    }
    if (!session) {
      return;
    }
    if (document_id) {
      let firstRoleId;
      if (templateId !== fallbackTempId) {
        const document: IDocument = await DocumentService.loadDocument(document_id);
        const firstRole = document?.recipients?.find(recipient => recipient.roleName !== '');
        firstRoleId = firstRole?.id;
      }
      const body = new FormData();
      body.append('action', 'save');
      body.append('document-id', document_id);
      body.append('recipient-email', session.email || '');
      body.append('recipient-first_name', session.first_name || '');
      body.append('recipient-last_name', session.last_name || '');
      body.append('recipient-role', 'signer');
      if (firstRoleId) {
        body.append('recipient-id', firstRoleId);
      }
      await Axios.post('/save/recipient', body);
    }

    return document_id;
  }

  static async loadTracking(documentId: string): Promise<IDocumentTracking> {
    const {
      data: { document },
    } = await apolloClient.query({
      query: documentTrackingQuery,
      variables: { documentId },
      fetchPolicy: EFetchPolicy.NoCache,
    });
    return document;
  }

  static async getPreviewToken(documentId: string): Promise<IPreviewToken> {
    const {
      data: { previewToken },
    } = await apolloClient.query({
      query: documentPreviewTokenQuery,
      variables: { documentId },
      fetchPolicy: EFetchPolicy.NoCache,
    });
    return previewToken;
  }

  static async createDocument(documentType: string): Promise<string> {
    const body = new FormData();
    body.append('action', 'data');
    body.append('document-type', documentType);
    const {
      data: { document_id },
    } = await Axios.post('/save/document', body);
    return document_id;
  }

  static async loadPageNums(documentId: string): Promise<Partial<IDocumentPage>[]> {
    const {
      data: {
        document: { pages },
      },
    } = await apolloClient.query({
      query: pageNumQuery,
      variables: { documentId },
      fetchPolicy: EFetchPolicy.NoCache,
    });
    return pages;
  }

  static async loadTemplateResourceDetails(contentId: string): Promise<Partial<IDocument>> {
    const {
      data: { document },
    } = await apolloClient.query({
      query: templateResourceDetailsQuery,
      variables: { contentId },
    });
    return document;
  }

  static async createPage(payload: ICreatePageDTO) {
    const {
      data: { addDocumentPage },
    } = await apolloClient.mutate({
      mutation: addDocumentPageMutation,
      variables: { ...payload },
    });
    return addDocumentPage;
  }

  static async deletePage(documentPageId: string): Promise<boolean> {
    const { data } = await apolloClient.mutate({
      mutation: removeDocumentPageMutation,
      variables: { id: documentPageId },
    });
    return data;
  }

  static async updatePage(dto: IUpdatePageDTO): Promise<IDocumentPage> {
    const { data } = await apolloClient.mutate({
      mutation: updateDocumentPageMutation,
      variables: dto,
    });
    return data.updateDocumentPage;
  }

  static async useTemplate(
    documentId: string,
    templateId: string,
    pageOffset: number
  ): Promise<IDocument> {
    const {
      data: { updateDocumentFromTemplate },
    } = await apolloClient.mutate({
      mutation: updateDocumentFromTemplateMutation,
      variables: {
        documentId,
        templateId,
        pageOffset,
      },
    });
    return updateDocumentFromTemplate;
  }

  static async getUploadFilesUrls(
    documentId: string,
    filenames: string[]
  ): Promise<IUploadFileUrl[]> {
    const { data } = await apolloClient.mutate({
      mutation: getUploadFilesUrlsMutation,
      variables: { documentId, filenames },
    });
    return data.getUploadFilesUrls;
  }

  static async uploadFiles(uploadFileUrls: IUploadFileUrl[], files: File[]): Promise<void> {
    await Promise.all(
      uploadFileUrls.map(async ({ uploadUrl }: IUploadFileUrl, index: number) => {
        return Axios.put(uploadUrl, files[index]);
      })
    );
  }

  static async uploadFilesDone(
    documentId: string,
    index: number,
    uniqueFilenames: string[]
  ): Promise<boolean> {
    const { data } = await apolloClient.mutate({
      mutation: uploadFilesDoneMutation,
      variables: { documentId, index, uniqueFilenames },
    });
    return data.uploadFilesDone;
  }

  static async loadExternalEditorId(documentId: string): Promise<string> {
    const {
      data: {
        document: { externalEditorId },
      },
    } = await apolloClient.query({
      query: externalEditorIdQuery,
      variables: { documentId },
      fetchPolicy: EFetchPolicy.NoCache,
    });
    return externalEditorId;
  }
}
